import React from 'react';
import { Form, Select, InputNumber, Button, Row, Col, Space, Alert, message } from 'antd';
import { PlusOutlined, SaveOutlined, SettingOutlined } from '@ant-design/icons';
import { COUNTRY_OPTIONS, fenToYuan } from '../../../../utils/regionPricing';

// ══════════════════════════════════════════════
//  定价块内的区域定价字段（非订阅售价 / 订阅售价 各一份）
//  字段约定：{prefix}TemplateId / {prefix}DefaultUsd / {prefix}CountryOverrides
// ──────────────────────────────────────────────

const RegionPricingFields = ({ form, prefix, templates = [], hasOverseas, onSaveTemplate, onManageTemplates }) => {
    const templateField = `${prefix}TemplateId`;

    // 选择模板 → 快速填充（便捷操作）
    const applyTemplate = (id) => {
        if (id === 'CUSTOM') return;
        const tpl = templates.find(t => t.id === id);
        if (!tpl) return;
        form.setFieldsValue({
            [`${prefix}DefaultUsd`]: fenToYuan(tpl.defaultUsd),
            [`${prefix}CountryOverrides`]: (tpl.countryOverrides || []).map(c => ({ country: c.country, usdPrice: fenToYuan(c.usdPrice) })),
        });
        message.success(`已应用模板「${tpl.name}」`);
    };

    return (
        <>
            <Row gutter={16}>
                <Col span={16}>
                    <Form.Item label="区域定价模板" name={templateField} style={{ marginBottom: 0 }}>
                        <Select
                            placeholder="选择模板快速填充，或自定义"
                            options={[
                                ...templates.map(t => ({ label: t.name, value: t.id })),
                                { label: '自定义', value: 'CUSTOM' },
                            ]}
                            onChange={applyTemplate}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    {/* label=" " 占位，保证按钮与左侧选择框水平对齐 */}
                    <Form.Item label=" " colon={false} style={{ marginBottom: 0 }}>
                        <Space>
                            <Button icon={<SaveOutlined />} onClick={onSaveTemplate}>另存为模板</Button>
                            <Button type="link" icon={<SettingOutlined />} onClick={onManageTemplates}>管理模板</Button>
                        </Space>
                    </Form.Item>
                </Col>
            </Row>

            <div style={{ height: 16 }} />

            <Form.Item
                label="海外默认价（$）"
                name={`${prefix}DefaultUsd`}
                rules={hasOverseas ? [{ required: true, message: '请输入海外默认价' }] : []}
                extra="海外未单独定价的国家按此价售卖；国内按上方 CNY 售价"
            >
                <InputNumber min={0.01} precision={2} controls={false} prefix="$" placeholder="0.00" style={{ width: '100%' }} />
            </Form.Item>

            <div style={{ fontSize: 13, color: '#606266', margin: '4px 0 12px' }}>国家覆盖（可选，覆盖海外默认价）</div>
            <Form.List name={`${prefix}CountryOverrides`}>
                {(fields, { add, remove }) => (
                    <div>
                        {fields.length === 0 && (
                            <div style={{ fontSize: 12, color: '#bfbfbf', marginBottom: 12 }}>未设置国家覆盖，海外统一按默认价售卖</div>
                        )}
                        {fields.map(({ key, name, ...restField }) => (
                            <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'baseline' }}>
                                <Form.Item
                                    {...restField}
                                    name={[name, 'country']}
                                    style={{ flex: 1, marginBottom: 0 }}
                                    rules={[
                                        { required: true, message: '请选择国家' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value) return Promise.resolve();
                                                const list = getFieldValue(`${prefix}CountryOverrides`) || [];
                                                const count = list.filter(r => r && r.country === value).length;
                                                return count > 1 ? Promise.reject(new Error('国家不可重复')) : Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <Select placeholder="选择国家" options={COUNTRY_OPTIONS} allowClear showSearch optionFilterProp="label" />
                                </Form.Item>
                                <Form.Item
                                    {...restField}
                                    name={[name, 'usdPrice']}
                                    style={{ flex: '0 0 200px', marginBottom: 0 }}
                                    rules={[{ required: true, message: '请输入售价' }]}
                                >
                                    <InputNumber min={0.01} precision={2} controls={false} prefix="$" placeholder="售价" style={{ width: '100%' }} />
                                </Form.Item>
                                <Button type="link" danger onClick={() => remove(name)}>删除</Button>
                            </div>
                        ))}
                        <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ country: undefined, usdPrice: undefined })}>
                            添加国家覆盖
                        </Button>
                    </div>
                )}
            </Form.List>

            {!hasOverseas && (
                <Alert type="warning" showIcon style={{ marginTop: 12 }} message="投放区域未选择海外，区域定价不生效（将按国内 CNY 售价售卖）" />
            )}
        </>
    );
};

export default RegionPricingFields;
